import { useCallback, useEffect, useRef } from 'react';
import { useInstantSearchContext } from 'react-instantsearch';

import {
  FacetPlacement,
  trackPlpFacetApplied,
} from '_utils/GA4/trackPlpFacetEvents';

import {
  getActiveFacetSummary,
  getAppliedFacets,
  getRefinementKeys,
} from './facetAppliedTracking';

type RefinementItems = Parameters<typeof getAppliedFacets>[1];

const hasSameKeys = (a: Set<string>, b: Set<string>) =>
  a.size === b.size && [...a].every((key) => b.has(key));

export const usePlpFacetAppliedTracking = ({
  enabled,
  items,
  counts,
  priceAttribute,
}: {
  enabled: boolean;
  items: RefinementItems;
  counts: Map<string, number>;
  priceAttribute: string;
}) => {
  const helper = useInstantSearchContext().mainIndex.getHelper();
  const interactionRef = useRef<FacetPlacement | null>(null);
  const pendingPlacementRef = useRef<FacetPlacement | null>(null);
  const prevKeysRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!helper) return undefined;

    const onChange = () => {
      if (interactionRef.current) {
        pendingPlacementRef.current = interactionRef.current;
      }
    };
    helper.on('change', onChange);
    return () => {
      helper.removeListener('change', onChange);
    };
  }, [helper]);

  useEffect(() => {
    const onPopState = () => {
      interactionRef.current = null;
      pendingPlacementRef.current = null;
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    const keys = getRefinementKeys(items);
    const prevKeys = prevKeysRef.current;
    prevKeysRef.current = keys;

    if (!enabled || !prevKeys || hasSameKeys(prevKeys, keys)) return;

    const placement = pendingPlacementRef.current;
    pendingPlacementRef.current = null;
    if (!placement) return;

    const summary = getActiveFacetSummary(counts, priceAttribute);
    getAppliedFacets(prevKeys, items, priceAttribute).forEach(
      ({ facetType, facetName }) =>
        trackPlpFacetApplied({
          data: {
            interactionLocation: window.location.pathname,
            facetType,
            facetName,
            facetPlacement: placement,
            ...summary,
          },
        })
    );
  }, [enabled, items, counts, priceAttribute]);

  return useCallback((placement: FacetPlacement) => {
    interactionRef.current = placement;

    setTimeout(() => {
      interactionRef.current = null;
    });
  }, []);
};
